import { Flex, Group, ScrollArea, SimpleGrid, Stack } from "@mantine/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AgentActivity } from "@/sdk";

import { useAnalytics } from "@/analytics";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useAgentFriendlyName, useAgentSdk, useAgentStore, useLogger } from "@/sdk";

import { CustomerLoveCard } from "./cards/CustomerLoveCard";
import { QualityEfficiencyCard } from "./cards/QualityEfficiencyCard";
import { SalesOpportunitiesCard } from "./cards/SalesOpportunitiesCard";
import { useDismissMorningPaper } from "./data/useMorningPaperDismissal";
import { useYesterdayPerformanceMetrics } from "./data/useYesterdayPerformanceMetrics";
import { AUTO_DISMISS_MS, AutoDismissCountdown } from "./greeting/AutoDismissCountdown";
import { Greeting } from "./greeting/Greeting";
import { MyMetricsHeader } from "./metrics/MyMetricsHeader";
import styles from "./morningPaper.module.css";
import { useMorningPaperShiftRitual } from "./store/useMorningPaperShiftRitual";

export function MorningPaper() {
    const firstName = useAgentFriendlyName();
    const { metrics, isLoading } = useYesterdayPerformanceMetrics();
    const { dispatcher } = useAnalytics();
    const { startShift } = useMorningPaperShiftRitual();
    const logger = useLogger({ module: "MorningPaper" });
    const sdk = useAgentSdk();
    const currentActivity = useAgentStore((state) => state.activity);
    const previousActivity = useRef<AgentActivity | undefined>(undefined);
    const hasDispatchedShown = useRef(false);
    const hasDispatchedMetricsLoaded = useRef(false);
    const [isDismissing, setIsDismissing] = useState(false);
    const isDismissingRef = useRef(false);
    const { mutateAsync: dismissMorningPaperMutation } = useDismissMorningPaper();
    const { performanceMetrics } = usePartnerConfig();

    // Determine which rating metric this partner uses (ERP or NPS)
    const ratingMetricType = useMemo(() => {
        const ratingConfig = performanceMetrics.find(
            (m) => m.metric === "entireRepPerformance" || m.metric === "fiveStarRating",
        );
        return ratingConfig?.metric === "fiveStarRating" ? "fiveStarRating" : "entireRepPerformance";
    }, [performanceMetrics]);

    const ratingValue = metrics?.[ratingMetricType] ?? null;

    useEffect(() => {
        if (hasDispatchedShown.current) return;
        hasDispatchedShown.current = true;
        void dispatcher.dispatchBusinessEvent("MorningPaperShown");
    }, [dispatcher]);

    // If the component unmounts for any reason (e.g. expert switches activity
    // via dropdown), mark the paper as dismissed so it doesn't flash again
    // when the expert later returns to Offline (e.g. end of shift).
    // Skip if already dismissing to avoid redundant POST on normal Start Shift path.
    useEffect(() => {
        return () => {
            if (isDismissingRef.current) return;
            void dismissMorningPaperMutation().catch((err) => {
                logger.error({ err }, "Morning paper: failed to record dismissal on unmount");
            });
        };
    }, [dismissMorningPaperMutation, logger]);

    useEffect(() => {
        if (isLoading || hasDispatchedMetricsLoaded.current) return;
        hasDispatchedMetricsLoaded.current = true;
        void dispatcher.dispatchBusinessEvent("MorningPaperMetricsLoaded", {
            hasRating: ratingValue != null,
            hasCrt: metrics?.callResolutionTime != null,
            hasSales: metrics?.netEnrolls != null,
        });
    }, [isLoading, metrics, dispatcher, ratingValue]);

    const handleDismiss = useCallback(async () => {
        if (isDismissingRef.current) return;
        isDismissingRef.current = true;
        setIsDismissing(true);
        try {
            void dispatcher.dispatchBusinessEvent("MorningPaperDismissed");
            await startShift();
            await dismissMorningPaperMutation();
        } catch (err: unknown) {
            logger.error({ err }, "Morning paper: dismiss handler failed");
            isDismissingRef.current = false;
            setIsDismissing(false);
        }
    }, [dispatcher, startShift, dismissMorningPaperMutation, logger, setIsDismissing]);

    const handleAutoDismiss = useCallback(() => {
        logger.info(
            { autoDismissMs: AUTO_DISMISS_MS },
            "Morning paper: auto-dismissing after inactivity timeout — flipping agent to Available",
        );
        void handleDismiss();
    }, [handleDismiss, logger]);

    useEffect(() => {
        const previous = previousActivity.current;
        previousActivity.current = currentActivity;

        if (!previous) return; // First commit — no transition to detect
        if (previous === sdk.availableAgentActivity) return;
        if (currentActivity !== sdk.availableAgentActivity) return; // Not transitioning to Available

        logger.info(
            { from: previous, to: currentActivity },
            "Morning paper: agent flipped to Available externally — auto-dismissing",
        );
        void handleDismiss();
    }, [currentActivity, sdk.availableAgentActivity, handleDismiss, logger]);

    return (
        <Flex className={styles.root} role="region" aria-label="Morning paper">
            <ScrollArea classNames={{ root: styles.scrollRoot, viewport: styles.scrollViewport }} type="auto">
                <Stack className={styles.content} gap="xl">
                    <Group justify="flex-end" className={styles.topBar}>
                        <AutoDismissCountdown durationMs={AUTO_DISMISS_MS} onTimeout={handleAutoDismiss} />
                    </Group>

                    <Greeting name={firstName} onDismiss={handleDismiss} isDismissing={isDismissing} />

                    <Stack gap="md">
                        <MyMetricsHeader />
                        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                            <CustomerLoveCard
                                view={{
                                    yesterdayRating: ratingValue,
                                    isLoading,
                                }}
                                metricType={ratingMetricType}
                            />
                            <QualityEfficiencyCard
                                view={{
                                    callResolutionTimeSeconds: metrics?.callResolutionTime ?? null,
                                    isLoading,
                                }}
                            />
                            <SalesOpportunitiesCard
                                view={{
                                    sales: metrics?.netEnrolls ?? null,
                                    isLoading,
                                }}
                            />
                        </SimpleGrid>
                    </Stack>
                </Stack>
            </ScrollArea>
        </Flex>
    );
}
